import React from 'react';
import Icon from '../../../components/AppIcon';

const ActivityTimeline = ({ data, loading }) => {
  const getHourlyActivity = () => {
    const hourlyCount = Array(24)?.fill(0);
    data?.forEach(log => {
      const hour = new Date(log?.timestamp)?.getHours();
      hourlyCount[hour]++;
    });
    return hourlyCount;
  };

  const hourlyData = getHourlyActivity();
  const maxActivity = Math.max(...hourlyData, 1);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-1/4 mb-6"></div>
        <div className="h-32 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="BarChart3" size={20} className="text-primary" />
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Activity Timeline</h3>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-success"></div>
            <span>Low Activity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-warning"></div>
            <span>Medium Activity</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-error"></div>
            <span>High Activity</span>
          </div>
        </div>
      </div>

      <div className="relative">
        <div className="flex items-end justify-between gap-1 h-32">
          {hourlyData?.map((count, hour) => {
            const heightPercent = (count / maxActivity) * 100;
            const getBarColor = () => {
              if (heightPercent > 70) return 'bg-error';
              if (heightPercent > 40) return 'bg-warning';
              return 'bg-success';
            };

            return (
              <div key={hour} className="flex-1 flex flex-col items-center group">
                <div className="relative w-full">
                  <div
                    className={`w-full rounded-t transition-all duration-300 hover:opacity-80 ${getBarColor()}`}
                    style={{ height: `${Math.max(heightPercent, 5)}%`, minHeight: '4px' }}
                  >
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-foreground text-background text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                      {hour}:00 - {count} actions
                    </div>
                  </div>
                </div>
                {hour % 3 === 0 && (
                  <span className="text-xs text-muted-foreground mt-2">{hour}:00</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="TrendingUp" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Peak Hour</span>
          </div>
          <p className="text-lg font-semibold text-foreground">
            {hourlyData?.indexOf(Math.max(...hourlyData))}:00
          </p>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Activity" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Total Actions</span>
          </div>
          <p className="text-lg font-semibold text-foreground">
            {hourlyData?.reduce((sum, count) => sum + count, 0)}
          </p>
        </div>
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Clock" size={16} className="text-primary" />
            <span className="text-xs text-muted-foreground">Avg per Hour</span>
          </div>
          <p className="text-lg font-semibold text-foreground">
            {(hourlyData?.reduce((sum, count) => sum + count, 0) / 24)?.toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ActivityTimeline;